/**
 * Store Pinia des échantillons de production (composition API)
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ServiceOperationResult } from '../models/api/serviceOperationResult';
import { IDataOperationResult } from '../models/api/dataOperationResult';
import DataOperationError from '../models/api/dataOperationError';
import { IProductionSample } from '../models/productionSample';
import { IProductionSampleApiResponse } from '../models/api/productionSampleApiResponse';
import { IProductionSampleForm } from '../models/forms/productionSampleForm';
import { IProductionSampleFilter } from '../models/filters/productionSampleFilter';
import ProductionSampleServices from '../services/ProductionSampleService';
import { useProductionStore } from './production.store';

/**
 * Store Pinia des échantillons de production utilisant la Composition API
 */
export const useProductionSampleStore = defineStore('ProductionSamples', () => {
    // State
    const datas = ref<IProductionSample[]>([]);
    const current = ref<IProductionSample>({} as IProductionSample);
    const itemForm = ref<IProductionSampleForm>({} as IProductionSampleForm);

    // Actions

    /**
     * Hydrate une liste d'échantillons en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param samples Liste d'échantillons reçus de l'API avec des IDs
     * @returns Liste d'échantillons avec les références des stores
     */
    async function hydrateSamples(samples: IProductionSampleApiResponse[]): Promise<IProductionSample[]> {
        if (samples.length === 0) return [];

        // NOTE: Ne PAS charger productionStore ici pour éviter la boucle circulaire
        // (productionStore.load → hydrateProductions → hydrateSamples → productionStore.load)

        // Hydrater en une seule passe
        const models: IProductionSample[] = [];
        for (const sample of samples) {
            const model = { ...sample } as unknown as IProductionSample;
            // NOTE: model.production n'est pas hydraté ici pour éviter la référence circulaire
            models.push(model);
        }
        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns Le résultat de l'opération
     */
    async function load(filter: IProductionSampleFilter) {
        const result = await ProductionSampleServices.getAll<IProductionSampleFilter, IProductionSampleApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateSamples(result.data as IProductionSampleApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id L'identifiant de l'élément à récupérer
     * @returns Le résultat de l'opération
     */
    async function get(id: number) {
        const result = await ProductionSampleServices.getById<IProductionSampleApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateSamples([result.data as IProductionSampleApiResponse]);
            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Sauvegarde d'un élément
     * @returns Le résultat de l'opération
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IProductionSampleApiResponse | IDataOperationResult<IProductionSampleApiResponse>>;
        if (itemForm.value.id > 0) {
            result = await ProductionSampleServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateSamples([result.data as IProductionSampleApiResponse]);
                current.value = modelData;
                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = 'Item non trouvé dans le store';
                    return new ServiceOperationResult<IDataOperationResult<IProductionSample>>(true, { errors: error });
                }
            }
        } else {
            result = await ProductionSampleServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateSamples([result.data as IProductionSampleApiResponse]);
                current.value = modelData;
                datas.value.push(current.value);
            }
        }
        return result;
    }

    /**
     * Mappage de l'élément current en formulaire
     * @param item L'élément à mapper
     * @returns Le formulaire mappé
     */
    function toForm(item: IProductionSample): IProductionSampleForm {
        const form = {} as IProductionSampleForm;
        Object.assign(form, item);
        form.productionId = item.production?.id;
        return form;
    }

    /**
     * Création d'un nouvel élément en formulaire
     */
    function newForm(lienId: number): IProductionSampleForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            isMarketable: false,
            hasObservation: false,
            isBlankSample: false,
            prcPredilution: 0,
            volume: 0,
            secondaryVolume: 0,
            absorbance: 0,
            secondaryAbsorbance: 0,
            concentration: 0,
            secondaryConcentration: 0,
            volumeToDelivered: 0
        } as IProductionSampleForm;
        return itemForm.value;
    }

    /**
     * Création d'un nouvel item
     */
    function newItem(): IProductionSample {
        current.value = {
            id: 0,
            productionId: 0,
            production: {} as any,
            isMarketable: false,
            hasObservation: false,
            isBlankSample: false,
            prcPredilution: 0,
            volume: 0,
            secondaryVolume: 0,
            absorbance: 0,
            secondaryAbsorbance: 0,
            concentration: 0,
            secondaryConcentration: 0,
            volumeToDelivered: 0
        } as IProductionSample;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,

        // Actions
        hydrateSamples,
        load,
        get,
        save,
        toForm,
        newForm,
        newItem
    };
});
